import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from './api';
import { APP_VERSION } from './version';
import './StatsPage.css';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) return dateStr;
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  } catch (e) {
    return dateStr;
  }
};

const placementEmoji = (placement) => {
  const n = Number(placement);
  if (n === 1) return '\u{1F947}';      // 🥇
  if (n === 2) return '\u{1F948}';      // 🥈
  if (n === 3) return '\u{1F949}';      // 🥉
  return '';
};

const BUILD_DATE = process.env.REACT_APP_BUILD_DATE || '';

function ResultsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [idInput, setIdInput] = useState(id || '');
  const [rawResults, setRawResults] = useState([]);
  const [eventDate, setEventDate] = useState(null);
  const [hideSeries, setHideSeries] = useState(false);
  const [hideNumber, setHideNumber] = useState(false);
  const [hideTeam, setHideTeam] = useState(false);
  const [academy, setAcademy] = useState('aogg');
  const [sortBy, setSortBy] = useState('placement');
  const [updateInfo, setUpdateInfo] = useState({ match_count: null, last_update: null });
  useEffect(() => {
    setIdInput(id || '');
    if (id) fetchResults(id);
    else setRawResults([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    api.get('/updated')
      .then(response => {
        const d = response?.data || {};
        const matchCount = d.match_count ?? d.matchCount;
        const lastUpdate = d.last_update ?? d.lastUpdate ?? d.date;
        setUpdateInfo({
          match_count: typeof matchCount === 'number' ? matchCount : (matchCount != null ? Number(matchCount) : null),
          last_update: lastUpdate != null && lastUpdate !== '' ? String(lastUpdate) : null,
        });
      })
      .catch(() => {
        setUpdateInfo({ match_count: null, last_update: null });
      });
  }, []);

  const extractResults = (data) => {
    return Array.isArray(data?.eventResults)
      ? data.eventResults
      : Array.isArray(data)
      ? data
      : [];
  };

  const fetchResults = async (eventId) => {
    if (!eventId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/api/results', {
        event_id: eventId,
        filters: {
          competitorname: null,
          groupname: null,
          academy: null,
          team: '',
          country: null,
          matCategory: [],
        },
      });
      setRawResults(extractResults(response?.data));
      setEventDate(response?.data?.event_date || null);
    } catch (err) {
      let message = 'Request failed';
      if (err?.response) {
        const detail = err.response?.data?.detail || err.response?.data?.message;
        if (err.response.status === 404) {
          message = `Tapahtumaa ${eventId} ei löytynyt Smoothcomista (404). Tarkista tapahtumanumero.`;
        } else {
          message = `HTTP ${err.response.status}${detail ? ': ' + detail : ''}`;
        }
      } else if (err?.message) {
        message = err.message;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const toPlacements = (results) => {
    const out = [];
    for (const item of results || []) {
      const series = item?.group?.name || '';
      const addEntrants = (arr) => {
        for (const e of arr || []) {
          const placement = e?.placement;
          const name = e?.target?.fullname || [e?.target?.firstname, e?.target?.lastname].filter(Boolean).join(' ').trim();
          if (!name) continue;
          const club = e?.club?.name || '';
          const affiliation = e?.affiliation?.name || '';
          out.push({ series, placement, name, club, affiliation });
        }
      };
      addEntrants(item?.top3);
      addEntrants(item?.after3);
    }
    return out;
  };

  const allPlacements = useMemo(() => toPlacements(rawResults), [rawResults]);

  const filtered = useMemo(() => {
    const filter = academy?.toLowerCase().trim();
    const matches = (p) => {
      if (!filter) return true;
      return (
        p.club?.toLowerCase().includes(filter) ||
        p.affiliation?.toLowerCase().includes(filter)
      );
    };
    const arr = allPlacements.filter(matches);
    const byName = (a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
    const byPlacement = (a, b) => {
      const pa = Number(a?.placement ?? Number.POSITIVE_INFINITY);
      const pb = Number(b?.placement ?? Number.POSITIVE_INFINITY);
      return pa - pb;
    };
    const bySeries = (a, b) => (a.series || '').localeCompare(b.series || '', undefined, { sensitivity: 'base' });
    const byClub = (a, b) => (a.club || '').localeCompare(b.club || '', undefined, { sensitivity: 'base' });
    switch (sortBy) {
      case 'name':
        arr.sort(byName);
        break;
      case 'series':
        arr.sort((a, b) => bySeries(a, b) || byPlacement(a, b) || byName(a, b));
        break;
      case 'team':
        arr.sort((a, b) => byClub(a, b) || byPlacement(a, b) || byName(a, b));
        break;
      default:
        arr.sort((a, b) => byPlacement(a, b) || byName(a, b));
    }
    return arr;
  }, [allPlacements, academy, sortBy]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!idInput) return;
    if (idInput !== id) navigate(`/results/${encodeURIComponent(idInput)}`);
    fetchResults(idInput);
  };

  const medalStats = useMemo(() => {
    const count = (arr) => {
      let gold = 0, silver = 0, bronze = 0, other = 0;
      for (const p of arr) {
        const n = Number(p.placement);
        if (n === 1) gold++;
        else if (n === 2) silver++;
        else if (n === 3) bronze++;
        else other++;
      }
      return { gold, silver, bronze, other, total: arr.length };
    };
    return {
      total: count(allPlacements),
      filtered: count(filtered),
    };
  }, [allPlacements, filtered]);

  return (
    <div className="stats-wrap">
      <button type="button" className="back-btn" onClick={() => navigate(-1)} title="Go back">&#8592; Back</button>
      <h2 className="page-title">Results {id ? `for event ${id}` : ''}</h2>
      {eventDate && (
        <div style={{ opacity: 0.6, fontSize: '0.9em', marginBottom: 8 }}>{formatDate(eventDate)}</div>
      )}
      <form onSubmit={handleSubmit} className="stats-controls">
        <div className="controls-row">
          <label className="control">
            <span className="label">Event id</span>
            <input
              className="input"
              type="number"
              value={idInput}
              onChange={(e) => setIdInput(e.target.value)}
              placeholder="e.g. 12345"
            />
          </label>
          <label className="control">
            <span className="label">Academy</span>
            <input
              className="input"
              type="text"
              value={academy}
              onChange={(e) => setAcademy(e.target.value)}
              placeholder="e.g. aogg"
            />
          </label>
          <label className="control">
            <span className="label">Sort by</span>
            <select
              className="input"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="placement">Placement</option>
              <option value="name">Name A-Z</option>
              <option value="series">Series, Placement</option>
              <option value="team">Team, Placement</option>
            </select>
          </label>
          <label className="control checkbox">
            <input
              type="checkbox"
              checked={hideNumber}
              onChange={(e) => setHideNumber(e.target.checked)}
            />
            <span>hide number</span>
          </label>
          <label className="control checkbox">
            <input
              type="checkbox"
              checked={hideTeam}
              onChange={(e) => setHideTeam(e.target.checked)}
            />
            <span>hide team</span>
          </label>
          <label className="control checkbox">
            <input
              type="checkbox"
              checked={hideSeries}
              onChange={(e) => setHideSeries(e.target.checked)}
            />
            <span>hide series</span>
          </label>
          <button type="submit" className="btn">Submit</button>
        </div>
      </form>

      {loading && <div>Loading…</div>}
      {error && (
        <div style={{ color: 'salmon', whiteSpace: 'pre-wrap' }}>{error}</div>
      )}
      {!loading && !error && rawResults.length > 0 && (
        <div style={{ marginBottom: 16, padding: '12px 16px', background: '#1a1a22', borderRadius: 8, fontSize: 14, lineHeight: 1.8 }}>
          <div>
            <strong>{'\u{1F947}'} {medalStats.total.gold}</strong>{'  '}
            <strong>{'\u{1F948}'} {medalStats.total.silver}</strong>{'  '}
            <strong>{'\u{1F949}'} {medalStats.total.bronze}</strong>{'  '}
            <span style={{ opacity: 0.6 }}>{medalStats.total.total} placements total in {rawResults.length} brackets</span>
          </div>
          {academy && (
            <div style={{ marginTop: 4 }}>
              <span style={{ color: '#a1a1aa' }}>
                {academy}: </span>
              <strong>{'\u{1F947}'} {medalStats.filtered.gold}</strong>{'  '}
              <strong>{'\u{1F948}'} {medalStats.filtered.silver}</strong>{'  '}
              <strong>{'\u{1F949}'} {medalStats.filtered.bronze}</strong>{'  '}
              <span style={{ opacity: 0.6 }}>{medalStats.filtered.total} placements ({Math.round(medalStats.filtered.total / medalStats.total.total * 100)}%)</span>
            </div>
          )}
        </div>
      )}
      {!loading && !error && filtered.length > 0 && (
        <div className="series-card">
          <div className="competitor-list">
            {filtered.map((p, i) => (
              <div key={i} className="competitor-item">
                {!hideNumber && (
                  <span style={{ minWidth: 28, opacity: 0.4, textAlign: 'center', fontSize: '0.8em' }}>{i + 1}</span>
                )}
                <span style={{ minWidth: 32, opacity: 0.9, textAlign: 'center' }}>
                  {placementEmoji(p.placement) || (
                    <span style={{ opacity: 0.5, fontSize: '0.85em' }}>{p.placement ?? ''}.</span>
                  )}
                </span>
                <Link className="competitor-name" to={`/${encodeURIComponent(p.name)}`} title={`Open ${p.name}`}>
                  {p.name}
                </Link>
                {p.club && !hideTeam && (
                  <span className="competitor-club" title={p.club}>
                    {p.club}
                  </span>
                )}
                {!hideSeries && p.series && (
                  <span style={{ flex: '1 1 0', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: 0.5, fontSize: '0.8em', textAlign: 'right' }} title={p.series}>
                    {p.series}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <footer className="footer">
        <span className="version-badge">
          Matches: {updateInfo.match_count != null ? updateInfo.match_count.toLocaleString() : '—'}, Last update {updateInfo.last_update ? formatDate(updateInfo.last_update) : '—'}, Build {BUILD_DATE || '—'}, {APP_VERSION}
        </span>
      </footer>
    </div>
  );
}

export default ResultsPage;
